import type { FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { createSilkField } from '@/lib/dots/fields'
import { DotCanvas } from '@/components/dots/dot-canvas'
import { SilkDrift } from '@/components/dots/silk-drift'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { SectionLabel } from '@/components/landing/section-label'
import { ContactSection } from '@/components/landing/contact-section'

const NEXT_STEPS = [
    {
        title: "We reply within a day",
        text: "A partner reads every message and answers within one business day, usually with a few questions about scope and timing."
    },
    {
        title: "A 30 minute call",
        text: "We talk through your goals, your users and your constraints. No slides, no sales pitch, just an honest read on the work."
    },
    {
        title: "A clear proposal",
        text: "Within a week you get a written plan with phases, team, timeline and a fixed price for the first milestone."
    }
]

const ContactPage: FC = (): ReactNode => {
    const field = useMemo(() => createSilkField(29, 0.12), [])

    return (
        <Layout>
            <Seo
                title='Contact'
                description="Start a project with Onda. Tell us about your product and a partner will reply within one business day."
            />

            <section id='top' className='relative isolate flex min-h-[72svh] items-end overflow-hidden bg-white'>
                <div aria-hidden='true' className='absolute inset-0 -z-10'>
                    <DotCanvas
                        field={field}
                        grid
                        interactive
                        centerX={0.68}
                        centerY={0.42}
                        mobileCenterX={0.55}
                        mobileCenterY={0.3}
                        speed={0.85}
                    />
                    <div className='pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-white via-white/80 to-transparent' />
                </div>

                <Container className='pt-32 pb-16 md:pb-24'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground motion-safe:animate-in fade-in duration-1000 fill-mode-both delay-300'>
                        Contact
                    </p>

                    <RevealText
                        as='h1'
                        immediate
                        lines={["Start a", "project."]}
                        delay={200}
                        stagger={90}
                        className='mt-6 text-[clamp(3.6rem,10vw,9rem)] font-medium leading-[0.88] tracking-[-0.06em] text-ink'
                    />

                    <p className='mt-8 max-w-md text-lg leading-relaxed text-ink/70 motion-safe:animate-in fade-in slide-in-from-bottom-4 duration-1000 fill-mode-both delay-700'>
                        New product, a platform rebuild or a second opinion on a hard problem. Tell us where you are and we will tell you honestly how we can help.
                    </p>
                </Container>
            </section>

            <section id='next-steps' className='relative isolate border-t border-line bg-white'>
                <SilkDrift />

                <Container className='py-24 md:py-32'>
                    <SectionLabel index='01'>What happens next</SectionLabel>

                    <ol className='mt-12 grid border-t border-l border-line md:grid-cols-3'>
                        {NEXT_STEPS.map((step, index) => (
                            <li key={step.title} className='border-r border-b border-line bg-white p-8 md:p-10'>
                                <Reveal delay={index * 120}>
                                    <span className='font-mono text-[11px] uppercase tracking-[0.14em] text-azure-600'>
                                        Step 0{index + 1}
                                    </span>

                                    <h2 className='mt-6 text-2xl font-medium tracking-[-0.03em] text-ink'>
                                        {step.title}
                                    </h2>

                                    <p className='mt-4 leading-relaxed text-ink/70'>
                                        {step.text}
                                    </p>
                                </Reveal>
                            </li>
                        ))}
                    </ol>
                </Container>
            </section>

            <ContactSection index='02' label="Write to us" lines={["Tell us about", "your project."]} />
        </Layout>
    )
}

export default ContactPage
